import type { StripeTextInputProps } from '@_/ui';
import type { ElementProps } from '@stripe/react-stripe-js';
import type { FormComponentProps } from '../formElements/types.ts';
import { createElement } from 'react';
import { StripeElementContainer, useStripeElementContainer } from '@_/ui';
import { useFieldContext, usePropsFromField } from '../formElements/index.ts';

type StripeElementProps<TProps> = FormComponentProps<
  TProps & Pick<StripeTextInputProps, 'disabled' | 'helpText' | 'label'>
>;

export const createFormStripeElement = <TProps extends ElementProps>(
  Component: React.ElementType<TProps>
) =>
  function FormStripeElement(props: StripeElementProps<TProps>) {
    const field = useFieldContext();

    const { disabled, error } = usePropsFromField(props, field);

    const {
      error: resolvedError,
      isFocused,
      stripeElementProps
    } = useStripeElementContainer({
      disabled,
      error,
      name: field.name,
      onBlur: field.handleBlur,
      onChange: e => field.handleChange(e.target.value)
    });

    return (
      <StripeElementContainer
        key={field.name}
        disabled={disabled}
        error={resolvedError}
        focused={isFocused}
        helpText={props.helpText}
        label={props.label}
      >
        {createElement(Component, stripeElementProps as TProps)}
      </StripeElementContainer>
    );
  };
